import { COMMA, ENTER } from '@angular/cdk/keycodes';
import {
  Component,
  computed,
  ElementRef,
  EventEmitter,
  Input,
  model,
  OnChanges,
  Output,
  Signal,
  signal,
  SimpleChanges,
  ViewChild,
  WritableSignal
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatAutocompleteModule, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatChipInputEvent, MatChipsModule } from '@angular/material/chips';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { IFilterItemDto } from '@common';

@Component({
  selector: 'app-chip-selector',
  imports: [FormsModule, MatAutocompleteModule, MatChipsModule, MatFormFieldModule, MatIconModule],
  templateUrl: './chip-selector.component.html',
  styleUrl: './chip-selector.scss'
})
export class ChipSelectorComponent implements OnChanges {
  //#region @Input/@Output ----------------------------------------------------
  @Input({ required: true }) allItems!: Array<IFilterItemDto>;
  @Input({ required: true }) label!: string;
  @Input({ required: true }) addLabel!: string;
  @Input() initialSelection?: Array<IFilterItemDto>;
  @Output() selectionChanged = new EventEmitter<Array<IFilterItemDto>>();

  @ViewChild('itemInput')
  private itemInput!: ElementRef<HTMLInputElement>;
  //#endregion ----------------------------------------------------------------

  //#region protected fields --------------------------------------------------
  protected readonly separatorKeysCodes: Array<number>;
  protected readonly currentItem = model<string>();
  protected readonly selectedItems: WritableSignal<Array<IFilterItemDto>>;
  protected readonly filteredItems: Signal<Array<IFilterItemDto>>;
  protected readonly allItemsSignal: WritableSignal<Array<IFilterItemDto>>;
  //#endregion

  //#region Constructor & C° --------------------------------------------------
  public constructor() {
    this.selectedItems = signal<Array<IFilterItemDto>>(new Array<IFilterItemDto>());
    this.allItemsSignal = signal<Array<IFilterItemDto>>(new Array<IFilterItemDto>());
    this.filteredItems = computed(() => {
      const enteredItem = this.currentItem()?.toLowerCase();
      // if (!enteredItem) {
      //   return new Array<IFilterItemDto>();
      // }
      return enteredItem
        ? this.allItemsSignal().filter((item: IFilterItemDto) => item.searchValue.toLowerCase().includes(enteredItem))
        : this.allItemsSignal().slice();
    });
    this.separatorKeysCodes = [ENTER, COMMA];
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['initialSelection']) {
      this.selectedItems.set([...this.initialSelection]);
    }

    if (changes['allItems']) {
      this.allItemsSignal.set(this.allItems);
    }
  }
  //#endregion

  //#region UI-Triggers -------------------------------------------------------
  protected tokenEnd(event: MatChipInputEvent): void {
    const value = event.value?.toLowerCase() || null;
    if (value) {
      const itemToAdd = this.allItems.find((item: IFilterItemDto) =>
        item.searchValue.toLocaleLowerCase().includes(value)
      );
      if (itemToAdd) {
        this.addOrRemoveItem(itemToAdd);
      }
    }
    this.currentItem.set('');
    this.itemInput.nativeElement.value = '';
  }

  protected removeItem(itemToRemove: IFilterItemDto): void {
    this.selectedItems.update((currentItems: Array<IFilterItemDto>) => {
      const index = currentItems.findIndex((item: IFilterItemDto) => item.searchValue == itemToRemove.searchValue);
      if (index < 0) {
        return currentItems;
      }
      currentItems.splice(index, 1);
      return [...currentItems];
    });
    this.selectionChanged.emit(this.selectedItems());
  }

  protected selectedItem(event: MatAutocompleteSelectedEvent): void {
    const select = this.allItems.find(
      (item: IFilterItemDto) => item.searchValue == (event.option.value as IFilterItemDto).searchValue
    );

    if (select) {
      this.addOrRemoveItem(select);
    }
    event.option.deselect();
    this.currentItem.set('');
  }

  protected isSelected(item: IFilterItemDto): boolean {
    return this.selectedItems().some((selected) => selected.key === item.key);
  }
  //#endregion

  //#region Auxiliary Methods -------------------------------------------------
  private addOrRemoveItem(item: IFilterItemDto): void {
    const alreadySelected = this.selectedItems().some((i: IFilterItemDto) => i.key === item.key);
    if (alreadySelected) {
      this.removeItem(item);
    } else {
      this.selectedItems.update((currentItems: Array<IFilterItemDto>) => [...currentItems, item]);
      this.selectionChanged.emit(this.selectedItems());
    }
  }
}
